"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import {
	BitrateControls,
	type BitrateConfig,
	DEFAULT_BITRATE,
} from "./BitrateControls";
import {
	DEFAULT_SELECTED,
	FORMATS,
	formatCoinCost,
} from "./formats";
import { FormatGrid } from "./FormatGrid";
import { MetadataPanel, type MetadataValue } from "./MetadataPanel";
import { PlatformPresetSelector } from "./PlatformPresetSelector";
import { PLATFORMS } from "./platforms";
import { EditorCore } from "@/core";
import {
	serializeProject,
	downloadSerializedFile,
} from "@/lib/nle-export";
import styles from "./ExportModal.module.css";

/**
 * ExportModal — Phase 25-AB Task 12.
 *
 * Multi-format export composite. Source-of-truth: docs/mockups/export.html.
 * Combines:
 *   - FormatGrid (26 formats / 6 categories)
 *   - PlatformPresetSelector (8 platform chips)
 *   - MetadataPanel (NEW · Task 18 #6 missing component)
 *   - BitrateControls (quality / bitrate / encoding / fps)
 *   - Watermark + Cloud sync toggles
 *
 * Selecting a platform chip auto-adds its recommended format to the
 * selection. The Export button is disabled when no format is selected.
 *
 * Uses createPortal to mount to <body> so it overlays the entire app.
 */

export interface ExportConfig {
	formats: string[];
	platforms: string[];
	metadata: MetadataValue;
	bitrate: BitrateConfig;
	options: {
		burnSubtitles: boolean;
		watermark: boolean;
		embedAssets: boolean;
		embedLicense: boolean;
		capcutAutoImport: boolean;
		cloudSync: boolean;
	};
}

export interface ExportModalProps {
	open: boolean;
	onClose: () => void;
	onExport?: (config: ExportConfig) => void;
	initialMetadata?: Partial<MetadataValue>;
	initialFormats?: string[];
}

const DEFAULT_METADATA: MetadataValue = {
	title: "",
	description: "",
	hashtags: [],
	category: "",
};

const DEFAULT_OPTIONS = {
	burnSubtitles: false,
	watermark: true,
	embedAssets: true,
	embedLicense: false,
	capcutAutoImport: true,
	cloudSync: false,
};

export function ExportModal({
	open,
	onClose,
	onExport,
	initialMetadata,
	initialFormats,
}: ExportModalProps) {
	const [mounted, setMounted] = useState(false);
	const [selectedFormats, setSelectedFormats] = useState<string[]>(
		initialFormats ?? [...DEFAULT_SELECTED],
	);
	const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([
		"youtube-shorts",
	]);
	const [metadata, setMetadata] = useState<MetadataValue>({
		...DEFAULT_METADATA,
		...initialMetadata,
	});
	const [bitrate, setBitrate] = useState<BitrateConfig>(DEFAULT_BITRATE);
	const [options, setOptions] = useState(DEFAULT_OPTIONS);
	const [search, setSearch] = useState("");
	const [exportResult, setExportResult] = useState<string | null>(null);
	const [metaGenerating, setMetaGenerating] = useState(false);
	// Phase 25 — user-tunable GIF options (was hardcoded fps=12 / width=480 /
	// duration=1s). Surfaced as a small inline form whenever "gif" is in
	// selectedFormats; defaults match the previous behaviour.
	const [gifFps, setGifFps] = useState(12);
	const [gifWidth, setGifWidth] = useState(480);
	const [gifDuration, setGifDuration] = useState(1.0);

	/**
	 * MetadataPanel "AI 자동 생성" — Increment 17. Calls the live Worker
	 * /ai/upload-meta endpoint and fills the metadata fields. Before this the
	 * onAutoGenerate prop was never passed, so the AI button never rendered.
	 */
	const handleAutoGenerateMeta = useCallback(async () => {
		if (metaGenerating) return;
		setMetaGenerating(true);
		try {
			const r = await fetch("/api/youtube-shorts/ai/upload-meta", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ title_hint: metadata.title }),
			});
			if (r.status === 401) {
				setExportResult("AI 메타 생성: 로그인이 필요합니다.");
				return;
			}
			const json = (await r.json().catch(() => ({}))) as {
				title?: string;
				description?: string;
				hashtags?: string[];
				category?: string;
				items?: { title?: string; description?: string; hashtags?: string[]; category?: string };
				error?: string;
			};
			if (!r.ok) {
				setExportResult(`AI 메타 생성 실패: ${json.error ?? r.status}`);
				return;
			}
			const m = json.items ?? json;
			setMetadata((prev) => ({
				title: m.title ?? prev.title,
				description: m.description ?? prev.description,
				hashtags: m.hashtags ?? prev.hashtags,
				category: m.category ?? prev.category,
			}));
			setExportResult("AI 메타데이터를 생성했습니다.");
		} catch (e) {
			setExportResult(`AI 메타 생성 오류: ${String(e)}`);
		} finally {
			setMetaGenerating(false);
		}
	}, [metadata.title, metaGenerating]);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (!open) return;
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	const handleToggleFormat = useCallback((key: string) => {
		setSelectedFormats((prev) =>
			prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
		);
	}, []);

	const handleTogglePlatform = useCallback((key: string) => {
		setSelectedPlatforms((prev) => {
			const next = prev.includes(key)
				? prev.filter((k) => k !== key)
				: [...prev, key];
			return next;
		});
		const preset = PLATFORMS.find((p) => p.key === key);
		if (preset) {
			setSelectedFormats((prev) =>
				prev.includes(preset.recommendedFormat)
					? prev
					: [...prev, preset.recommendedFormat],
			);
		}
	}, []);

	const handleToggleOption = useCallback(
		(key: keyof typeof DEFAULT_OPTIONS) => {
			setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
		},
		[],
	);

	const removeSelected = useCallback((key: string) => {
		setSelectedFormats((prev) => prev.filter((k) => k !== key));
	}, []);

	const selectedFormatObjs = useMemo(
		() => FORMATS.filter((f) => selectedFormats.includes(f.key)),
		[selectedFormats],
	);

	const totalCoins = useMemo(
		() =>
			selectedFormatObjs.reduce(
				(sum, f) => sum + formatCoinCost(f.cost),
				0,
			),
		[selectedFormatObjs],
	);

	const freeCount = selectedFormatObjs.filter((f) => f.cost === "free").length;
	const paidCount = selectedFormatObjs.length - freeCount;

	/** mp4 format key -> OpenCut renderer quality (Increment 19). */
	const MP4_QUALITY: Record<string, "medium" | "high" | "very_high"> = {
		"mp4-720p": "medium",
		"mp4-1080p": "high",
		"mp4-4k": "very_high",
	};

	async function handleExport() {
		const config: ExportConfig = {
			formats: selectedFormats,
			platforms: selectedPlatforms,
			metadata,
			bitrate,
			options,
		};
		onExport?.(config);

		// Generate + download the formats this app can serialize
		// (EDL/FCPXML/OTIO/SRT/VTT/TXT/JSON/CSV/CapCut/Premiere/Shotcut).
		const editor = EditorCore.getInstance();
		const project = editor.project.getActiveOrNull();
		if (!project) {
			setExportResult(
				"활성 프로젝트가 없습니다 — 에디터에서 프로젝트를 연 뒤 내보내세요.",
			);
			return;
		}
		const done: string[] = [];
		const skipped: string[] = [];
		for (const key of selectedFormats) {
			const file = serializeProject(key, project);
			if (file) {
				downloadSerializedFile(file);
				done.push(key);
			} else if (!MP4_QUALITY[key]) {
				skipped.push(key);
			}
		}

		// Increment 19 — real video render: mp4-720p/1080p/4k go through the
		// OpenCut renderer (editor.project.export -> canvas -> MediaRecorder).
		const mp4Keys = selectedFormats.filter((k) => MP4_QUALITY[k]);
		for (const key of mp4Keys) {
			setExportResult(`${key} 렌더링 중… (잠시 걸립니다)`);
			try {
				const result = await editor.project.export({
					options: {
						format: "mp4",
						quality: MP4_QUALITY[key],
						fps: project.settings.fps,
						includeAudio: true,
					},
				});
				if (result.success && result.buffer) {
					downloadSerializedFile({
						filename: `${project.metadata.name || "export"}-${key}.mp4`,
						content: "",
						mimeType: "video/mp4",
						blob: new Blob([result.buffer], { type: "video/mp4" }),
					});
					done.push(key);
				} else if (result.cancelled) {
					skipped.push(`${key}(취소됨)`);
				} else {
					skipped.push(`${key}(${result.error ?? "render 실패"})`);
				}
			} catch (e) {
				skipped.push(`${key}(${String(e)})`);
			}
		}

		// Phase 25 — GIF export. Hybrid: prefer the user's local Helper EXE
		// (ffmpeg palettegen+paletteuse on the user's machine); only when
		// Helper is unavailable do we hit the server engine. Either way the
		// output is real palettegen+paletteuse quality, not naive scale=gif.
		if (selectedFormats.includes("gif")) {
			setExportResult("GIF 인코딩 중… (mp4 렌더 + Helper/서버 처리)");
			try {
				const mp4 = await editor.project.export({
					options: {
						format: "mp4",
						quality: "medium",
						fps: project.settings.fps,
						includeAudio: false,
					},
				});
				if (mp4.success && mp4.buffer) {
					const blob = new Blob([mp4.buffer], { type: "video/mp4" });
					// Local Helper path first.
					const { helperEncodeGif } = await import("@/lib/helper-engines");
					const helperRes = await helperEncodeGif({
						file: new File([blob], `${project.metadata.name || "export"}.mp4`, {
							type: "video/mp4",
						}),
						fps: gifFps,
						width: gifWidth,
						durationSec: gifDuration,
					});
					if (helperRes.ok && helperRes.downloadUrl) {
						const dl = await fetch(helperRes.downloadUrl);
						if (dl.ok) {
							downloadSerializedFile({
								filename: `${project.metadata.name || "export"}.gif`,
								content: "",
								mimeType: "image/gif",
								blob: await dl.blob(),
							});
							done.push("gif (local Helper)");
							// Skip the server-side fallback when Helper succeeded.
							const _consumed = mp4;
							void _consumed;
							throw new Error("__helper_gif_done__");
						}
					}
					if (helperRes.error && helperRes.error !== "helper_unavailable") {
						skipped.push(`gif(Helper 실패: ${helperRes.error.slice(0, 60)})`);
						const _consumed = mp4;
						void _consumed;
						throw new Error("__helper_gif_done__");
					}
					// Server fallback (no Helper installed).
					const fd = new FormData();
					fd.append("media", blob, `${project.metadata.name || "export"}.mp4`);
					const up = await fetch("/api/youtube-shorts/ai/upload-media", {
						method: "POST",
						body: fd,
					});
					const upJson = (await up.json().catch(() => ({}))) as {
						path?: string;
						error?: string;
					};
					if (up.ok && upJson.path) {
						const r = await fetch("/api/youtube-shorts/ai/gif-encode", {
							method: "POST",
							headers: { "Content-Type": "application/json" },
							body: JSON.stringify({
								video_path: upJson.path,
								fps: gifFps,
								width: gifWidth,
								start: 0,
								duration: gifDuration,
							}),
						});
						const gifResp = (await r.json().catch(() => ({}))) as {
							output_path?: string;
							error?: string;
						};
						if (r.ok && gifResp.output_path) {
							const dl = await fetch(
								`/api/youtube-shorts/ai/wizard-output?path=${encodeURIComponent(gifResp.output_path)}`,
							);
							if (dl.ok) {
								const gifBlob = await dl.blob();
								downloadSerializedFile({
									filename: `${project.metadata.name || "export"}.gif`,
									content: "",
									mimeType: "image/gif",
									blob: gifBlob,
								});
								done.push("gif");
							} else {
								skipped.push(`gif(다운로드 실패 ${dl.status})`);
							}
						} else {
							skipped.push(`gif(${gifResp.error ?? "encode 실패"})`);
						}
					} else {
						skipped.push(`gif(업로드 실패 ${upJson.error ?? up.status})`);
					}
				} else {
					skipped.push(`gif(mp4 render 실패 ${mp4.error ?? ""})`);
				}
			} catch (e) {
				const msg = String(e);
				// Sentinel from the Helper-success branch — already accounted for.
				if (!msg.includes("__helper_gif_done__")) {
					skipped.push(`gif(${msg})`);
				}
			}
		}

		const parts: string[] = [];
		if (done.length > 0)
			parts.push(`${done.length}개 다운로드: ${done.join(", ")}`);
		if (skipped.length > 0)
			parts.push(
				`${skipped.length}개 미완 (벤더 전용/render 미지원): ${skipped.join(", ")}`,
			);
		setExportResult(parts.join(" · ") || "선택된 형식이 없습니다.");
	}

	if (!open || !mounted) return null;

	return createPortal(
		<div
			className={styles.backdrop}
			onClick={onClose}
			role="presentation"
			data-testid="export-modal-backdrop"
		>
			<div
				className={styles.modal}
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-label="내보내기"
				data-testid="export-modal"
				{...devLabelPanel("18")}
			>
				<header className={styles.header} {...devLabel("18-A")}>
					<h1 className={styles.title}>내보내기 · Multi-format Export</h1>
					<button
						type="button"
						className={styles.closeBtn}
						onClick={onClose}
						aria-label="닫기"
						data-testid="export-close"
					>
						×
					</button>
				</header>

				<div className={styles.body}>
					<main className={styles.center}>
						<FormatGrid
							selected={selectedFormats}
							onToggle={handleToggleFormat}
							search={search}
							onSearchChange={setSearch}
						/>
						{selectedFormats.includes("gif") && (
							<div
								data-testid="gif-options"
								style={{
									marginTop: 14,
									padding: "12px 14px",
									border: "1px solid rgba(212,175,55,0.4)",
									borderRadius: 8,
									background: "rgba(212,175,55,0.06)",
								}}
							>
								<div
									style={{
										fontSize: 11,
										fontWeight: 600,
										color: "#d4af37",
										marginBottom: 10,
									}}
								>
									GIF 옵션
								</div>
								<GifSlider
									label="길이 (초)"
									min={0.5}
									max={10}
									step={0.5}
									value={gifDuration}
									suffix="s"
									onChange={setGifDuration}
									testId="gif-duration"
								/>
								<GifSlider
									label="FPS"
									min={5}
									max={30}
									step={1}
									value={gifFps}
									suffix=" fps"
									onChange={(v) => setGifFps(Math.round(v))}
									testId="gif-fps"
								/>
								<GifSlider
									label="가로 너비"
									min={120}
									max={1280}
									step={20}
									value={gifWidth}
									suffix=" px"
									onChange={(v) => setGifWidth(Math.round(v))}
									testId="gif-width"
								/>
							</div>
						)}
					</main>

					<aside className={styles.right} {...devLabelPanel("21")}>
						{/* Selected list */}
						<div className={styles.rightBlock} {...devLabelPanel("21-A")}>
							<div className={styles.rightTitle}>
								선택 항목 ({selectedFormats.length})
							</div>
							<div className={styles.selectedList} data-testid="selected-list">
								{selectedFormatObjs.length === 0 && (
									<div className={styles.selectedEmpty}>
										선택된 형식이 없습니다.
									</div>
								)}
								{selectedFormatObjs.map((f) => (
									<div
										key={f.key}
										className={styles.selRow}
										data-testid={`selected-${f.key}`}
									>
										<span className={styles.selName}>{f.label}</span>
										<span className={styles.selCost}>
											{f.cost === "free" ? "무료" : `+${f.cost}`}
										</span>
										<button
											type="button"
											className={styles.selX}
											onClick={() => removeSelected(f.key)}
											aria-label={`${f.label} 제거`}
										>
											×
										</button>
									</div>
								))}
							</div>
						</div>

						<PlatformPresetSelector
							selected={selectedPlatforms}
							onToggle={handleTogglePlatform}
						/>

						<BitrateControls value={bitrate} onChange={setBitrate} />

						<div className={styles.rightBlock} {...devLabelPanel("21-D")}>
							<div className={styles.rightTitle}>옵션</div>
							{(
								[
									["burnSubtitles", "자막 burn-in", "자막을 영상에 합치기"],
									["watermark", "워터마크 (무료)", "우측 하단 MML 로고"],
									[
										"embedAssets",
										"폰트/이미지 embed",
										"CapCut 폴더에 모두 포함",
									],
									[
										"embedLicense",
										"CC 라이선스 embed",
										"사용자 + 저작권 메타",
									],
									[
										"capcutAutoImport",
										"CapCut 자동 import",
										"앱 실행 시 즉시 표시",
									],
									[
										"cloudSync",
										"Cloud Sync R2 (30일)",
										"재다운로드 가능",
									],
								] as const
							).map(([k, label, sub]) => (
								<div className={styles.optRow} key={k}>
									<div className={styles.optLabelCol}>
										<span className={styles.optLabel}>{label}</span>
										<span className={styles.optSub}>{sub}</span>
									</div>
									<button
										type="button"
										className={[
											styles.optToggle,
											options[k] ? styles.optToggleOn : "",
										]
											.filter(Boolean)
											.join(" ")}
										onClick={() => handleToggleOption(k)}
										role="switch"
										aria-checked={options[k]}
										aria-label={label}
										data-testid={`option-${k}`}
									/>
								</div>
							))}
						</div>

						<MetadataPanel
							value={metadata}
							onChange={setMetadata}
							onAutoGenerate={handleAutoGenerateMeta}
						/>

						<div className={styles.rightBlock} {...devLabelPanel("21-G")}>
							<div className={styles.summary}>
								<div className={styles.sumRow}>
									<span>기본 형식 (무료)</span>
									<span className={styles.sumVal}>{freeCount} 항목</span>
								</div>
								<div className={styles.sumRow}>
									<span>크레딧 차감 형식</span>
									<span className={styles.sumVal}>{paidCount} 항목</span>
								</div>
								<div className={`${styles.sumRow} ${styles.sumTotal}`}>
									<span>총 차감 코인</span>
									<span className={styles.sumVal}>{totalCoins} C</span>
								</div>
							</div>
						</div>

						<div className={styles.actions} {...devLabelPanel("21-H")}>
							<button
								type="button"
								className={styles.btnExport}
								onClick={handleExport}
								disabled={selectedFormats.length === 0}
								data-testid="export-start"
							>
								내보내기 시작 ({totalCoins} 코인)
							</button>
							<button
								type="button"
								className={styles.btnCancel}
								onClick={onClose}
								data-testid="export-cancel"
							>
								취소
							</button>
						</div>

						{exportResult && (
							<div
								data-testid="export-result"
								role="status"
								onClick={() => setExportResult(null)}
								style={{
									marginTop: 8,
									padding: "8px 12px",
									borderRadius: 6,
									border: "1px solid #d4af37",
									background: "rgba(212,175,55,0.08)",
									color: "#d4af37",
									fontSize: 11,
									lineHeight: 1.5,
									cursor: "pointer",
								}}
							>
								{exportResult}
							</div>
						)}
					</aside>
				</div>
			</div>
		</div>,
		document.body,
	);
}

/**
 * GifSlider — Phase 25 P3 inline GIF options form helper. Small range
 * control with a value readout; matches the gold-on-dark studio look.
 */
function GifSlider({
	label,
	min,
	max,
	step,
	value,
	suffix,
	onChange,
	testId,
}: {
	label: string;
	min: number;
	max: number;
	step: number;
	value: number;
	suffix: string;
	onChange: (v: number) => void;
	testId: string;
}) {
	return (
		<div style={{ marginBottom: 8 }}>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					fontSize: 11,
					opacity: 0.85,
					marginBottom: 4,
				}}
			>
				<span>{label}</span>
				<span data-testid={`${testId}-val`}>
					{value}
					{suffix}
				</span>
			</div>
			<input
				type="range"
				min={min}
				max={max}
				step={step}
				value={value}
				onChange={(e) => onChange(parseFloat(e.target.value))}
				data-testid={testId}
				style={{ width: "100%", accentColor: "#d4af37" }}
			/>
		</div>
	);
}
